import { SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "../../atoms/Button";
import { InputDate } from "../../atoms/Input/InputDate";
import { InputRadio } from "../../atoms/Input/InputRadio";
import { InputText } from "../../atoms/Input/InputText";
import * as S from "./surveyForm.style";
import { useMutation } from "@tanstack/react-query";
import {
  addSurveyApi,
  deletePreviewImagesApi,
  uploadImagesApi,
} from "../../../apis/surveyApi";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { InputFile } from "../../atoms/Input/InputFile";
import {
  eightWalkRelativeEvaluation,
  gripstrengthRelativeEvaluationL,
  gripstrengthRelativeEvaluationR,
  sitNupRelativeEvaluation,
  standingHighjumpEvaluation,
} from "../../../lib/relativeEvaluation";
import { schema } from "./schema";

interface BmiState {
  pat_bodylength: number | null;
  pat_weight: number | null;
  pat_bmi: number | null;
}

interface SurveyForm {
  disable: boolean;
}

export const SurveyForm = ({ disable }: SurveyForm) => {
  const navigate = useNavigate();

  const [imgPreview, setImgPreview] = useState();
  const [bmiInfo, setBmiInfo] = useState<BmiState>({
    pat_bodylength: null,
    pat_weight: null,
    pat_bmi: null,
  });
  const [error, setError] = useState();
  const [phoneNumber, setPhoneNumber] = useState("");

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  /** 설문제출 onclick 핸들러 */
  const onSubmit: SubmitHandler<any> = (data) => {
    const today = new Date();
    const birthDate = new Date(data.pat_birthdate); // 2000년 8월 10일

    // 만나이 계산식
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    data.pat_age = age;
    // 제출 시 전화번호 하이픈 제거
    data.pat_phone = data.pat_phone.replace(/[^0-9]/g, "");
    /** 수정 저장  */

    // 8자보행 상대평가 기준 산출
    const eightWalk = eightWalkRelativeEvaluation(
      data.pat_sex,
      data.pat_age,
      data.pat_eightwalk
    );
    // 앉았다 일어서기 상대평가 기준 산출
    const sitNup = sitNupRelativeEvaluation(
      data.pat_sex,
      data.pat_age,
      data.pat_sit_n_up
    );

    // 왼쪽 상대 악력
    const LgripAverage =
      ((data.pat_left_gripstrength_01 +
        data.pat_left_gripstrength_02 +
        data.pat_left_gripstrength_03) /
        3 /
        data.pat_weight) *
      100;

    const LgripAverageResult = LgripAverage.toFixed(2);
    const Lgripstrength = gripstrengthRelativeEvaluationL(
      data.pat_sex,
      data.pat_age,
      Number(LgripAverageResult)
    );

    // 오른쪽 악력 평균
    const RgripAverage =
      ((data.pat_right_gripstrength_01 +
        data.pat_right_gripstrength_02 +
        data.pat_right_gripstrength_03) /
        3 /
        data.pat_weight) *
      100;
    const RgripAverageResult = RgripAverage.toFixed(2);
    const Rgripstrength = gripstrengthRelativeEvaluationR(
      data.pat_sex,
      data.pat_age,
      Number(RgripAverageResult)
    );

    /** 제자리 높이 뛰기 상대평가 등급 */
    if (age < 65) {
      const standingHighjump = standingHighjumpEvaluation(
        data.pat_sex,
        data.pat_age,
        data.pat_standing_highjump
      );
      data.pat_standing_highjump_eval = standingHighjump;
    } else {
      data.pat_standing_highjump_eval = null;
    }

    data.pat_left_gripstrength_eval = Lgripstrength;
    data.pat_right_gripstrength_eval = Rgripstrength;

    data.pat_sit_n_up_eval = sitNup;
    data.pat_eightwalk_eval = eightWalk;

    // react-hook-form에 의해 안들어가는 bmi 값 수정 필요
    data.pat_bmi = bmiInfo.pat_bmi;

    const datas = {
      files: imgPreview,
      content: data,
    };
    addSurveyMutation.mutate(datas);
  };

  /** 설문지 제출 mutation */
  const addSurveyMutation = useMutation(["addSurveyApi"], addSurveyApi, {
    onSuccess() {
      navigate("/main");
    },
    onError(err: any) {
      const { response } = err;
      setError(response.data.message);
    },
  });

  /** 전화번호 자동 하이픈 함수 */
  const phoneNumberReg = (number: string) => {
    setPhoneNumber(
      number
        .replace(/[^0-9]/g, "")
        .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, "$1-$2-$3")
        .replace(/(\-{1,2})$/g, "")
    );
  };

  /** 이미지 추가버튼  */
  const onChangeImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const imageFormData = new FormData();
    [].forEach.call(e.target.files, (f: any) => {
      imageFormData.append("files", f);
    });
    uploadImagesApi(imageFormData).then((result) => {
      setImgPreview(result);
    });
  };

  /** DB에 올라가지 않은 미리보기 이미지 삭제 */
  const onRemovePreviewImage = (
    v: any,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    deletePreviewImagesApi(v.filename).then(() => {
      setImgPreview(undefined);
    });
  };

  useEffect(() => {
    // bmi값을 구하기 위한 키와 몸무게 값이 다들어오면 bmi 계산
    if (bmiInfo.pat_bodylength && bmiInfo.pat_weight) {
      //bmi계산식 toFixed( bmi 소수점 두자리 수까지만 남김 )
      const bmi = (
        bmiInfo.pat_weight /
        ((bmiInfo.pat_bodylength / 100) * (bmiInfo.pat_bodylength / 100))
      ).toFixed(2);
      setBmiInfo({
        ...bmiInfo,
        pat_bmi: Number(bmi),
      });
    } else {
      setBmiInfo({
        ...bmiInfo,
        pat_bmi: null,
      });
    }
  }, [bmiInfo.pat_weight, bmiInfo.pat_bodylength]);

  return (
    <S.SurveyForm onSubmit={handleSubmit(onSubmit)}>
      <S.SurveyItems>
        <S.SurveySubTit>기본 정보</S.SurveySubTit>
        <S.ItemWrapper>
          <InputText
            label="아이디"
            layout="column"
            type="text"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_id")}
            disable={disable}
            error={errors}
            name="pat_id"
          />
          <InputText
            label="비밀번호"
            layout="column"
            type="password"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_password")}
            disable={disable}
            error={errors}
            name="pat_password"
          />
          <InputText
            label="발사이즈"
            layout="column"
            type="number"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_foot_size")}
            disable={disable}
            error={errors}
            name="pat_foot_size"
          />
          <InputText
            label="이름"
            layout="column"
            type="text"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_name")}
            disable={disable}
            error={errors}
            name="pat_name"
          />
          <InputText
            label="전화번호"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            type="text"
            placeholder="입력..."
            value={phoneNumber}
            register={register("pat_phone")}
            onChange={({ target }: any) => phoneNumberReg(target.value)}
            disable={disable}
            error={errors}
            name="pat_phone"
          />
          <InputDate
            label="생년월일"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            defaultValue={"1960-01-01"}
            register={register("pat_birthdate")}
            disable={disable}
            error={errors}
            name="pat_birthdate"
          />
          <InputRadio
            title="성별"
            themeType="admin"
            layout="column"
            size="xlg"
            register={register("pat_sex")}
            disable={disable}
            error={errors}
            name="pat_sex"
          />
        </S.ItemWrapper>
      </S.SurveyItems>

      <S.SurveyItems>
        <S.SurveySubTit>신체 조성</S.SurveySubTit>
        <S.ItemWrapper>
          <InputText
            label="신장(cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            value={bmiInfo.pat_bodylength}
            register={register("pat_bodylength")}
            disable={disable}
            onChange={({ target }: any) =>
              setBmiInfo({ ...bmiInfo, pat_bodylength: target.value })
            }
            error={errors}
            name="pat_bodylength"
          />
          <InputText
            label="체중(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            value={bmiInfo.pat_weight}
            register={register("pat_weight")}
            disable={disable}
            onChange={({ target }: any) =>
              setBmiInfo({ ...bmiInfo, pat_weight: target.value })
            }
            error={errors}
            name="pat_weight"
          />
          <InputText
            label="BMI"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            value={bmiInfo.pat_bmi}
            register={register("pat_bmi")}
            disable={true}
          />
          <InputText
            label="체지방률(%Fay)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_bodyfat_percentage")}
            error={errors}
            disable={disable}
            name="pat_bodyfat_percentage"
          />
          <InputText
            label="근육량(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_amount_musle")}
            error={errors}
            disable={disable}
            name="pat_amount_musle"
          />
          <InputText
            label="골격근(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_bone_musle")}
            error={errors}
            disable={disable}
            name="pat_bone_musle"
          />
        </S.ItemWrapper>
      </S.SurveyItems>

      <S.SurveyItems>
        <S.SurveySubTit>건강 체력</S.SurveySubTit>
        <S.ItemWrapper>
          <InputText
            label="악력-왼쪽 1차(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_left_gripstrength_01")}
            error={errors}
            disable={disable}
            name="pat_left_gripstrength_01"
          />
          <InputText
            label="악력-오른쪽 1차(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_right_gripstrength_01")}
            error={errors}
            disable={disable}
            name="pat_right_gripstrength_01"
          />
          <InputText
            label="악력-왼쪽 2차(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_left_gripstrength_02")}
            error={errors}
            disable={disable}
            name="pat_left_gripstrength_02"
          />
          <InputText
            label="악력-오른쪽 2차(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_right_gripstrength_02")}
            error={errors}
            disable={disable}
            name="pat_right_gripstrength_02"
          />
          <InputText
            label="악력-왼쪽 3차(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_left_gripstrength_03")}
            error={errors}
            disable={disable}
            name="pat_left_gripstrength_03"
          />
          <InputText
            label="악력-오른쪽 3차(kg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_right_gripstrength_03")}
            error={errors}
            disable={disable}
            name="pat_right_gripstrength_03"
          />
          <InputText
            label="앉았다 일어서기(회)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_sit_n_up")}
            error={errors}
            disable={disable}
            name="pat_sit_n_up"
          />
          <InputText
            label="8자 보행(초)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_eightwalk")}
            error={errors}
            disable={disable}
            name="pat_eightwalk"
          />
          <InputText
            label="제자리 높이 뛰기(체공시간/초)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_standing_highjump")}
            error={errors}
            disable={disable}
            name="pat_standing_highjump"
          />
        </S.ItemWrapper>
      </S.SurveyItems>

      <S.SurveyItems>
        <S.SurveySubTit>보행 패턴</S.SurveySubTit>
        <S.ItemWrapper>
          <S.SurveyPatternTit>목 신전·굴곡</S.SurveyPatternTit>
          <InputText
            label="앞 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_neck_flexion_front")}
            error={errors}
            disable={disable}
            name="pat_neck_flexion_front"
          />
          <InputText
            label="뒤 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_neck_flexion_back")}
            error={errors}
            disable={disable}
            name="pat_neck_flexion_back"
          />

          <S.SurveyPatternTit>몸통 전후 기울기</S.SurveyPatternTit>
          <InputText
            label="앞 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_bodybf_incline_front")}
            error={errors}
            disable={disable}
            name="pat_bodybf_incline_front"
          />
          <InputText
            label="뒤 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_bodybf_incline_back")}
            error={errors}
            disable={disable}
            name="pat_bodybf_incline_back"
          />

          <S.SurveyPatternTit>고관절 신전·굴곡</S.SurveyPatternTit>
          <InputText
            label="오른쪽 앞 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_flexion_r_front")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_flexion_r_front"
          />
          <InputText
            label="오른쪽 뒤 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_flexion_r_back")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_flexion_r_back"
          />
          <InputText
            label="왼쪽 앞 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_flexion_l_front")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_flexion_l_front"
          />
          <InputText
            label="왼쪽 뒤 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_flexion_l_back")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_flexion_l_back"
          />

          <S.SurveyPatternTit>고관절 회전 각도</S.SurveyPatternTit>
          <InputText
            label="오른쪽 내 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_rotation_angle_r_in")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_rotation_angle_r_in"
          />
          <InputText
            label="오른쪽 외(deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_rotation_angle_r_out")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_rotation_angle_r_out"
          />
          <InputText
            label="왼쪽 내(deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_rotation_angle_l_in")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_rotation_angle_l_in"
          />
          <InputText
            label="왼쪽 외(deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_hipjoint_rotation_angle_l_out")}
            error={errors}
            disable={disable}
            name="pat_hipjoint_rotation_angle_l_out"
          />

          <S.SurveyPatternTit>무릎관절 신전·굴곡</S.SurveyPatternTit>
          <InputText
            label="좌 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_kneejoint_flexion_l")}
            error={errors}
            disable={disable}
            name="pat_kneejoint_flexion_l"
          />
          <InputText
            label="우 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_kneejoint_flexion_r")}
            error={errors}
            disable={disable}
            name="pat_kneejoint_flexion_r"
          />

          <S.SurveyPatternTit>몸통 좌우 기울기</S.SurveyPatternTit>
          <InputText
            label="좌 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_bodylr_incline_l")}
            error={errors}
            disable={disable}
            name="pat_bodylr_incline_l"
          />
          <InputText
            label="우 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_bodylr_incline_r")}
            error={errors}
            disable={disable}
            name="pat_bodylr_incline_r"
          />
          <S.SurveyPatternTit>골반 좌우 이동 변위</S.SurveyPatternTit>
          <InputText
            label="좌 (cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_pelvis_lr_movment_l")}
            error={errors}
            disable={disable}
            name="pat_pelvis_lr_movment_l"
          />
          <InputText
            label="우 (cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_pelvis_lr_movment_r")}
            error={errors}
            disable={disable}
            name="pat_pelvis_lr_movment_r"
          />

          <S.SurveyPatternTit>수직 이동 변위</S.SurveyPatternTit>
          <InputText
            label="위 (cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_vertical_movement_up")}
            error={errors}
            disable={disable}
            name="pat_vertical_movement_up"
          />

          <S.SurveyPatternTit>골반 회전 각도</S.SurveyPatternTit>
          <InputText
            label="좌 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_pelvic_rotation_angle_l")}
            error={errors}
            disable={disable}
            name="pat_pelvic_rotation_angle_l"
          />
          <InputText
            label="우 (deg)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_pelvic_rotation_angle_r")}
            error={errors}
            disable={disable}
            name="pat_pelvic_rotation_angle_r"
          />

          <S.SurveyPatternTit>보간·보폭</S.SurveyPatternTit>

          <InputText
            label="보폭-좌 (cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_stride_l")}
            error={errors}
            disable={disable}
            name="pat_stride_l"
          />
          <InputText
            label="보폭-우 (cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_stride_r")}
            error={errors}
            disable={disable}
            name="pat_stride_r"
          />
          <InputText
            label="보간(cm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_step_length")}
            error={errors}
            disable={disable}
            name="pat_step_length"
          />
        </S.ItemWrapper>
      </S.SurveyItems>

      <S.SurveyItems>
        <S.SurveySubTit>Gate View</S.SurveySubTit>
        <S.ItemWrapper>
          <InputText
            label="ENV(mm²)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_env")}
            error={errors}
            disable={disable}
            name="pat_env"
          />
          <InputText
            label="REC(mm²)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_rec")}
            error={errors}
            disable={disable}
            name="pat_rec"
          />
          <InputText
            label="RMS(mm²)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_rms")}
            error={errors}
            disable={disable}
            name="pat_rms"
          />
          <InputText
            label="TLC(mm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_tlc")}
            error={errors}
            disable={disable}
            name="pat_tlc"
          />
          <InputText
            label="Total Length(mm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_total_length")}
            error={errors}
            disable={disable}
            name="pat_total_length"
          />
          <InputText
            label="Sway velccity(mm/s)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_sway_velccity")}
            error={errors}
            disable={disable}
            name="pat_sway_velccity"
          />
          <InputText
            label="Length/ENV(mm)"
            layout="column"
            themeType="admin"
            size="xlg"
            width="100%"
            placeholder="입력..."
            register={register("pat_length_env")}
            error={errors}
            disable={disable}
            name="pat_length_env"
          />
        </S.ItemWrapper>
        <InputFile
          label="이미지 등록"
          id="image"
          onChangeImages={onChangeImages}
          onRemovePreviewImage={onRemovePreviewImage}
          imgPreview={imgPreview}
          disable={disable}
        />
      </S.SurveyItems>

      <Button
        type="submit"
        color="primary"
        layout="solid"
        width="100%"
        size="xlg"
        label="저장"
      />
    </S.SurveyForm>
  );
};
